import React, { useEffect, useState, useRef } from "react";
import BSL_Dialog from "../../components/BSL_Dialog";
import { API_BASE } from "../../AppConfig";
import { getCookie } from "../../components/Common";

export default function NonStandardDeliveryCheck({
    siteParameters,
    poa,
    account,
    isNonStandardDelivery,
    setIsNonStdDelivery,
    hasAcknowledged,
    setHasAcknowledged,
    setLoading,
    error,
    setError,
    displayElement,
}) {
    const [dismissMessage, setDismissMessage] = useState(false);

    const postCode = siteParameters?.Address?.PostCode;
    const town = siteParameters?.Address?.Suburb;
    const state = siteParameters?.Address?.State;
    const area = siteParameters?.NonStandardDeliveryArea;

    const lastFeeInputsRef = useRef(`${poa}|${account}`);

    useEffect(() => {
        if (!postCode || !town || !state || !poa || !account) return;

        const abort = new AbortController();
        const areaId = [town, state, postCode].filter(Boolean).join("").toLowerCase().replace(/\s+/g, "");
        const feeKey = `${poa}|${account}`;
        const feeInputsUnchanged = lastFeeInputsRef.current === feeKey;
        const hasDetails = area != null;

        if (hasDetails && feeInputsUnchanged) {
            if (area?.CurrentId === areaId) {
                const result = area?.AreaHistory?.find((item) => item.Id === areaId);
                setHasAcknowledged(result?.AcknowledgedAt != null);
                setIsNonStdDelivery(true);
                return () => abort.abort();
            }
        }

        setLoading(true);
        setError(false);
        setDismissMessage(false);

        const fetchFee = async () => {
            try {
                const token = window?.debug === true ? getCookie("CDS_token") : null;

                const res = await fetch(API_BASE() + "deliveryfee", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { "X-CDS_token": token } : {}),
                    },
                    body: JSON.stringify({
                        PostCode: postCode,
                        State: state,
                        Town: town,
                        POA: poa,
                        Account: account,
                    }),
                    signal: abort.signal,
                    redirect: "follow",
                    referrer: "no-referrer",
                    mode: "cors",
                    cache: "no-cache",
                    credentials: "same-origin",
                });

                if (!res.ok) throw new Error("Failed to fetch delivery fee");

                const data = await res.json();
                if (data?.NormalDeliveryCharge > 0) {
                    setIsNonStdDelivery(false);
                } else {
                    setIsNonStdDelivery(true);
                }
            } catch (err) {
                if (err.name === "AbortError") return;
                setError(true);
            } finally {
                setHasAcknowledged(false);
                setLoading(false);
            }
        };

        fetchFee();

        return () => abort.abort();
    }, [postCode, state, town, poa, account]);

    function handleAcknowledge() {
        setHasAcknowledged(true);

        requestAnimationFrame(() => {
            if (displayElement?.current) {
                displayElement.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        });
    }

    return (
        <>
            {isNonStandardDelivery && !hasAcknowledged && !error && (
                <BSL_Dialog
                    title="Acknowledge Delivery Charge"
                    open={true}
                    onClose={handleAcknowledge}
                    button1Text="Acknowledge"
                    button1Click={handleAcknowledge}
                >
                    <div style={{ display: "flex" }}>
                        <div>
                            This address falls outside our standard delivery area and will incur an additional charge.
                        </div>
                    </div>
                </BSL_Dialog>
            )}

            {error && !dismissMessage && (
                <BSL_Dialog
                    title="Unable to Determine Delivery Area"
                    open={true}
                    onClose={() => setDismissMessage(true)}
                    button1Text="Ok"
                    button1Click={() => setDismissMessage(true)}
                >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div>We've been unable to determine if this job falls within a standard delivery area.</div>
                        <br />
                        <div>Please continue and we'll prompt you to try again later.</div>
                    </div>
                </BSL_Dialog>
            )}
        </>
    );
}
